"use client"

import { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"
import type { Dispatch, SetStateAction } from "react"
import { useChat } from "@ai-sdk/react"
import { lastAssistantMessageIsCompleteWithToolCalls, isTextUIPart } from "ai"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import type { UIMessage } from "ai"
import { effectiveColorway, executeTool, type MenuIconName, type MenuOption, type UsageRow, type FabricRow, type BlockInfoOut, type BlockOut, type LayoutOut, type CellOut, type SandwichPlan, type CapacityReport, type RenderedBlock, type RenderedQuilt, type RenderedThumbnail, type Artifact } from "@/lib/tools"
import { TOOL_OUTPUT_HANDLERS, applySynthesis, type SynthesisRequest } from "@/lib/synthesis"
import { renderBlock, DEFAULT_BLOCK_COLORS } from "@/lib/core/block_svg"
import { getSchema, schemaSlots } from "@/lib/core/blockschema"
import { inFraction } from "@/lib/core/plan_utils"
import { distributionLabel } from "@/lib/core/layout"
import { fromDict } from "@/lib/core/state"
import type { Block, Project } from "@/lib/core/state"
import PinToggle from "./PinToggle"
import { loomDocFromArtifact, pinIdFor } from "@/lib/pins"
import {
  Plus, Pencil, Trash2, Square, LayoutGrid, Grid3x3,
  Scissors, FileText, Palette, Tag, Ruler, Package,
  Info, Wand2, Dices, Printer, Play, ArrowRight, Download,
  ChevronLeft, ChevronRight,
} from "lucide-react"
import {
  listProjectDocs, loadProjectDoc, saveProjectDoc,
  getCurrentDocId, setCurrentDocId,
  deriveDocKind,
  EMPTY_TOOL_OUTPUTS,
  type LoomDoc, type ProjectDoc,
} from "@/lib/storage"
import type { ToolName } from "@/lib/tool-schemas"
import { useVoiceInput } from "@/lib/voice"
import { currentValue, type Dimension } from "@/lib/core/dimensions"
import { PHASES, dimensionsForPhase, resolvePhase, type Phase } from "@/lib/core/phases"
import PhaseStrip from "./PhaseStrip"
import ZoomableViewer from "./ZoomableViewer"
import BudgetBar from "./BudgetBar"
import { IconButton } from "./ui/icon-button"
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip"

interface ChatPaneProps {
  // The whole loom document (project + transcript + cached tool outputs).
  // ChatPane never persists anything itself — every change funnels through
  // onDocChange so the parent can write the doc atomically as one IDB record.
  doc: LoomDoc
  onDocChange: Dispatch<SetStateAction<LoomDoc>>
  onReset?: () => void
  onLoadDoc?: (doc: LoomDoc) => void
  // Pin state — owned by the page so the scrapbook (sibling of ChatPane)
  // can share the same set of pinned IDs without duplicating IDB reads.
  pinnedIds: Set<string>
  onTogglePin: (artifact: Artifact, id: string) => void
}

const WELCOME = "Threaded and ready. Pick from the menu — or describe a quilt."

// Every entry must read as a stand-alone first message — an opener.
// No mid-conversation commands ("half the yardage", "name it") that
// assume an existing project.
//
// Each seed carries an optional `phase` tag — the phase strip at the top of
// the menu filters the pool to (phase-tagged for the active phase) ∪
// (untagged ambient seeds), so switching tabs actually changes the menu
// instead of just the highlight.
type Seed = MenuOption & { phase?: Phase }
const SEED_POOL: Seed[] = [
  // Themed quilts — fabric/mood-driven, opener for the Fabrics phase
  { text: "Safari quilt", swatches: ["#c4956a", "#efe7d2", "#5d7a5e"], phase: "fabrics" },
  { text: "Baby quilt in soft pastels", swatches: ["#f7c5cc", "#cfeede", "#fff2c2"], phase: "fabrics" },
  { text: "Moody autumn quilt", swatches: ["#8a3a1e", "#c98a1e", "#1f4d4f"], phase: "fabrics" },
  { text: "Beach quilt: sand, sea glass, coral", swatches: ["#e8d9a8", "#9fc6b8", "#e89b8c"], phase: "fabrics" },
  { text: "Twilight quilt: indigo, lavender, charcoal", swatches: ["#2c3e7b", "#b8a4d4", "#3a3a3a"], phase: "fabrics" },
  { text: "Jungle quilt: greens and brass", swatches: ["#3a5a2c", "#7a8a3c", "#b8923a"], phase: "fabrics" },
  { text: "Citrus quilt: orange, lemon, white", swatches: ["#f4d35e", "#ee964b", "#ffffff"], phase: "fabrics" },
  { text: "Prairie quilt: wheat, sky, cedar", swatches: ["#d4b87a", "#a8c8e0", "#7a4f2a"], phase: "fabrics" },
  { text: "Desert sunset quilt", swatches: ["#c46a4a", "#e8c69a", "#d49aa0"], phase: "fabrics" },
  { text: "Cottage floral quilt", swatches: ["#c89aa0", "#9aa884", "#f0e6d2"], phase: "fabrics" },
  { text: "Black and white quilt", swatches: ["#000000", "#888888", "#ffffff"], phase: "fabrics" },
  { text: "Christmas quilt: deep red, evergreen, gold", swatches: ["#7a1f1f", "#1f4d2e", "#c9a14a"], phase: "fabrics" },
  { text: "Picnic quilt with gingham vibes", swatches: ["#c43a3a", "#ffffff", "#3a5fa8"], phase: "fabrics" },
  { text: "Memory quilt for Grandma", swatches: ["#d4b59a", "#a89880", "#efe7d2"], phase: "fabrics" },
  { text: "Halloween quilt: black, orange, plum", swatches: ["#1a1a1a", "#d97a2c", "#5a2a4a"], phase: "fabrics" },
  { text: "Easter quilt in pastels", swatches: ["#f4c8d9", "#c8e0c4", "#f0d9a8"], phase: "fabrics" },
  { text: "Wedding quilt in cream and gold", swatches: ["#f5ecd9", "#caa14a", "#a88c5a"], phase: "fabrics" },
  { text: "What can I make from my stash?", icon: "fabric", phase: "fabrics" },
  { text: "What's WOF?", icon: "info", phase: "fabrics" },
  { text: "Pick the weirdest colorway you can", icon: "colors", phase: "fabrics" },
  // Block-style openers and per-block construction walkthroughs
  { text: "How does the Sawtooth Star work?", icon: "info", phase: "blocks" },
  { text: "Plan a Nine-Patch quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Pinwheel quilt", icon: "block", phase: "blocks" },
  { text: "Plan an Ohio Star quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Rail Fence quilt with the zig-zag pattern", icon: "block", phase: "blocks" },
  { text: "Plan a Single Irish Chain quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Double Irish Chain quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Log Cabin quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Courthouse Steps quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Churn Dash quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Shoofly quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Friendship Star quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Classic Rail Fence quilt", icon: "block", phase: "blocks" },
  { text: "Plan a Bow Tie quilt", icon: "block", phase: "blocks" },
  { text: "Surprise me from the nine-patch family", icon: "surprise", phase: "blocks" },
  { text: "Compare two block styles side by side", icon: "block", phase: "blocks" },
  { text: "Show me a sample block", icon: "block", phase: "blocks" },
  { text: "Show me the cut sizes for one block", icon: "info", phase: "blocks" },
  { text: "Walk me through no-waste flying geese", icon: "info", phase: "blocks" },
  { text: "Walk me through two-at-a-time HSTs", icon: "info", phase: "blocks" },
  { text: "Walk me through four-at-a-time QSTs", icon: "info", phase: "blocks" },
  { text: "Walk me through strip-piecing rails", icon: "info", phase: "blocks" },
  // Layout, distribution, setting, sizing — opener for the Blanket phase
  { text: "Plan a sampler quilt with a different block in every cell", icon: "block", phase: "blanket" },
  { text: "Make a Barn Raising arrangement with my Log Cabin blocks", icon: "layout", phase: "blanket" },
  { text: "Plan an on-point quilt with setting triangles", icon: "layout", phase: "blanket" },
  { text: "Try a dithered cascade layout", icon: "layout", phase: "blanket" },
  { text: "Plan a quilt with sashing and cornerstones", icon: "layout", phase: "blanket" },
  { text: "Plan a pinwheel of pinwheels", icon: "block", phase: "blanket" },
  { text: "Make the stars spin across the quilt", icon: "layout", phase: "blanket" },
  { text: "Plan a wall hanging", icon: "measure", phase: "blanket" },
  { text: "Plan a king-size quilt", icon: "measure", phase: "blanket" },
  { text: "Plan a lap throw", icon: "measure", phase: "blanket" },
  { text: "How much fabric for a queen?", icon: "measure", phase: "blanket" },
  { text: "What are setting triangles?", icon: "info", phase: "blanket" },
  // Finishing — binding, backing, batting
  { text: "How much binding, backing, and batting do I need?", icon: "measure", phase: "finishing" },
  // Ambient — playful openers shown on every phase
  { text: "Surprise me", icon: "surprise" },
  { text: "Make a quilt my grandmother would love" },
  { text: "Make me a quilt with a ridiculous name", icon: "name" },
  { text: "Quickest quilt I can finish" },
  { text: "Quilt for a cat to sit on" },
]

const CATEGORY_ICONS: Record<Dimension["category"], MenuIconName> = {
  block:   "block",
  layout:  "layout",
  palette: "colors",
  color:   "colors",
  fabric:  "fabric",
}

// One sub-menu per dimension (not per category): tapping "Block style" opens
// a sub-menu of style values; tapping "Block unit" opens its own sub-menu.
// The previous shape collapsed every dimension in a category into one flat
// bag, which mixed style values and unit values together. Here each
// dimension keeps its own row at the phase's root and its values stay
// scoped under it.
//
// Each value-row carries a `synth` descriptor instead of an onSelect closure
// — the click dispatcher picks it up and runs the synthesizer. Keeping the
// menu builder closure-free lets the items be safely captured into
// submenuStack without going stale w.r.t. the latest doc state.
function dimensionMenuRows(
  dimensions: Dimension[],
  project: Project,
): MenuOption[] {
  const rows: MenuOption[] = []
  for (const dim of dimensions) {
    if (!dim.values) continue
    if (dim.applicability && !dim.applicability(project)) continue
    const cur = currentValue(dim, project)
    const children: MenuOption[] = []
    for (const v of dim.values) {
      if (v.value === cur) continue
      const text = v.label
      const disabledReason = v.disabledReason?.(project) ?? null
      if (disabledReason) {
        children.push({ text, disabledReason })
        continue
      }
      const { name, args, chain } = dim.tool(v.value)
      children.push({
        text,
        synth: {
          text,
          toolName: name,
          args,
          chain: chain?.map((c) => ({ toolName: c.name, args: c.args })),
        },
      })
    }
    if (children.length === 0) continue
    rows.push({
      text: dim.label,
      icon: CATEGORY_ICONS[dim.category],
      children,
    })
  }
  return rows
}

// Fabrics-tab Colorway submenu: one row per slot of the active block schema,
// each row a hidden <input type="color"> the renderer wraps with a <label>.
// Tapping a row opens the native picker; on `change`, ChatPane dispatches
// colorway_set + render_block via handleColorChange — the same chain
// LOOM_004's swatch row uses, so both paths end at "block recolors instantly."
//
// Group "A" matches handleColorChange's default — pre-fabric users are
// setting a preview palette, not a per-group colorway. Multi-group
// (Colorway → Group A / Group B → slots) is intentionally deferred until
// fabrics-per-group lands; the swatch row has the same single-group scope.
//
// Lives outside the DIMENSIONS table on purpose: DIMENSIONS entries enumerate
// a finite `values` list, and a slot's hex is continuous + per-schema. Forcing
// it in would either pre-pick a palette per slot (a different product call)
// or special-case a "color picker" value-shape the renderer would still need
// to branch on. Keeping it here, parallel to dimensionMenuRows, is honest.
function colorwayMenuRows(project: Project): MenuOption[] {
  const slots = schemaSlots(getSchema(project.block.style))
  if (slots.length === 0) return []
  const colors = effectiveColorway(project, "A")
  const children: MenuOption[] = slots.map((slot) => {
    const current = colors[slot]
    return {
      // Capitalize the slot key — same convention as BlockColorSwatches' label
      // span (CSS `capitalize`). Slots are role keys ("center"/"points"), not
      // patch names, so patchLabel doesn't apply.
      text: slot.charAt(0).toUpperCase() + slot.slice(1),
      // Single-dot swatch in the icon column reflects current state, so the
      // submenu reads as colored chips, not a wall of text.
      swatches: [current],
      colorPicker: { slot, current },
    }
  })
  return [{ text: "Colorway", icon: "colors", children }]
}

function pickRandom<T>(items: T[], n: number): T[] {
  const copy = [...items]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy.slice(0, n)
}

// Per-tool side-effect handlers and the synthesis helper live in lib/synthesis
// so the menu's deterministic taps and the LLM's tool-call effect path share
// one registry. Adding a key to ClientRenderResults still forces a new
// handler — the exhaustive type lives in synthesis.ts.

export default function ChatPane({ doc, onDocChange, onReset, onLoadDoc, pinnedIds, onTogglePin }: ChatPaneProps) {
  const [input, setInput] = useState("")
  const [submenuStack, setSubmenuStack] = useState<{ title: string; items: MenuOption[] }[]>([])
  const [savedProjects, setSavedProjects] = useState<ProjectDoc[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Mirror of the live doc, used to read current state inside effects without
  // closure staleness. Updated synchronously alongside every onDocChange call
  // so a follow-up effect read sees the just-set value.
  const docRef = useRef(doc)
  useEffect(() => { docRef.current = doc }, [doc])

  const { project, transcript, toolOutputs } = doc
  const { blockSvgs, diagramSvgs, quiltSvgs, quiltGrids, planMarkdowns, pendingOptions } = toolOutputs

  // Tool-call IDs already executed by this component instance. Each id runs
  // through executeTool exactly once even if the executor effect re-runs
  // (StrictMode mount, post-addToolOutput re-render, sendAutomatically continuation).
  const handledToolCalls = useRef<Set<string>>(new Set())

  const { messages, sendMessage, addToolOutput, setMessages, status } = useChat({
    messages: transcript,
    sendAutomaticallyWhen: ({ messages: msgs }) => {
      if (!lastAssistantMessageIsCompleteWithToolCalls({ messages: msgs })) return false
      const last = msgs.at(-1)
      if (!last || last.role !== "assistant") return false
      let lastToolName: string | undefined
      for (const p of last.parts) {
        if (p.type === "dynamic-tool") lastToolName = p.toolName
      }
      // show_menu is a UI affordance, not a tool the model needs to react to.
      // Skip the continuation round-trip so the chat returns to ready and the
      // menu rows stay enabled.
      if (lastToolName === "show_menu") return false
      return true
    },
  })

  // Single combined effect: execute pending tool calls, fold their typed
  // outputs into doc.toolOutputs via TOOL_OUTPUT_HANDLERS, and mirror the live
  // transcript into doc — all in one onDocChange call so the persisted
  // snapshot is always coherent. Persistence happens in the parent on every
  // doc change.
  useEffect(() => {
    if (status !== "ready") return

    const lastMessage = messages.at(-1)
    const pending = lastMessage?.role === "assistant"
      ? lastMessage.parts.filter(
          (p) =>
            p.type === "dynamic-tool" &&
            p.state === "input-available" &&
            !handledToolCalls.current.has(p.toolCallId)
        )
      : []

    // Mark handled before any work so re-entry (StrictMode, re-renders
    // triggered by addToolOutput) is a no-op for already-processed ids.
    for (const p of pending) {
      if (p.type === "dynamic-tool") handledToolCalls.current.add(p.toolCallId)
    }

    let next = docRef.current
    const outputs: Array<{ tool: string; toolCallId: string; output: unknown }> = []

    for (const part of pending) {
      if (part.type !== "dynamic-tool") continue
      const { toolCallId, toolName } = part
      const toolInput = part.input as Record<string, unknown>
      const { result, updatedProject } = executeTool(toolName as ToolName, toolInput, next.project)

      if (updatedProject) next = { ...next, project: updatedProject }

      let modelOutput: unknown = result
      const handler = (TOOL_OUTPUT_HANDLERS as Record<string, (r: unknown, id: string, d: LoomDoc) => LoomDoc>)[toolName]
      if (handler) {
        next = handler(result, toolCallId, next)
        modelOutput = { ok: true }
      }

      outputs.push({ tool: toolName, toolCallId, output: modelOutput })
    }

    // Always mirror useChat's live messages into the doc so the persisted
    // transcript matches what the user sees.
    if (next.transcript !== messages) {
      next = { ...next, transcript: messages }
    }

    if (next !== docRef.current) {
      docRef.current = next
      onDocChange(next)
    }

    for (const o of outputs) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      addToolOutput({ tool: o.tool as any, toolCallId: o.toolCallId, output: o.output })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, status])

  // Auto-scroll to the latest content. Coalesced through rAF so a burst of
  // streaming-chunk re-renders fires at most one scroll per frame, and uses
  // "instant" because token-by-token "smooth" cancellations stack into a
  // sustained CPU loop (LOOM_017).
  const scrollRafRef = useRef<number | null>(null)
  useEffect(() => {
    if (scrollRafRef.current != null) return
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" })
    })
    return () => {
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current)
        scrollRafRef.current = null
      }
    }
  }, [messages])

  // The user's chosen phase wins; null means "follow derivation."
  // Switching phase resets the submenu stack so the user lands at the
  // root of the new phase's menu, not a stale submenu from the prior phase.
  const activePhase = resolvePhase(project, doc.phase)
  const setPhase = useCallback((phase: Phase) => {
    setSubmenuStack([])
    onDocChange((prev) => ({ ...prev, phase }))
  }, [onDocChange])

  // A menu tap that writes a deterministic event into the transcript.
  // Reads docRef so successive taps build on each other rather than on the
  // closure-captured project at submenu-open time. Closes the submenu so
  // the action feels committed; the user can reopen to keep tweaking.
  const synthesize = useCallback((req: SynthesisRequest) => {
    if (status === "streaming" || status === "submitted") return
    const next = applySynthesis(docRef.current, req)
    docRef.current = next
    onDocChange(next)
    setMessages(next.transcript)
    setSubmenuStack([])
  }, [onDocChange, setMessages, status])

  const clearPendingOptions = useCallback(() => {
    onDocChange((prev) =>
      prev.toolOutputs.pendingOptions.length === 0
        ? prev
        : { ...prev, toolOutputs: { ...prev.toolOutputs, pendingOptions: [] } }
    )
  }, [onDocChange])

  // Click-through color picker: tapping a swatch under a rendered block
  // synthesizes colorway_set + render_block as one transcript event, so the
  // user sees the recolored block immediately without an AI round-trip.
  // Targets group "A" — the default when there are no fabrics yet (the
  // beginner / preview-palette case LOOM_004 calls out).
  //
  // When a quilt is sized (rows*cols > 0), append render_quilt so the most
  // recent quilt visualization stays consistent with the freshly recolored
  // block (REC_001: re-render is the closing step of every state-mutation
  // chain). Without this the user sees a stale pre-recolor quilt above a
  // newly-colored block — they contradict each other in the same scroll view.
  const handleColorChange = useCallback((slot: string, hex: string) => {
    const { rows, cols } = docRef.current.project.layout
    const chain: NonNullable<SynthesisRequest["chain"]> = [{ toolName: "render_block", args: {} }]
    if (rows > 0 && cols > 0) chain.push({ toolName: "render_quilt", args: {} })
    synthesize({
      text: `Set ${slot} to ${hex}`,
      toolName: "colorway_set",
      args: { group: "A", colors: { [slot]: hex } },
      chain,
    })
  }, [synthesize])

  const handleSendText = useCallback((text: string) => {
    if (!text || status === "streaming" || status === "submitted") return
    setInput("")
    clearPendingOptions()
    sendMessage({ text })
  }, [sendMessage, status, clearPendingOptions])

  const handleSend = useCallback(() => {
    handleSendText(input.trim())
  }, [input, handleSendText])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // ── Sub-menu navigation ─────────────────────────────────────────────────────

  const openSubmenu = useCallback((title: string, items: MenuOption[]) => {
    setSubmenuStack((prev) => [...prev, { title, items }])
  }, [])

  const closeSubmenu = useCallback(() => {
    setSubmenuStack((prev) => prev.slice(0, -1))
  }, [])

  const refreshProjects = useCallback(async () => {
    const docs = await listProjectDocs()
    setSavedProjects(docs)
  }, [])

  useEffect(() => {
    listProjectDocs().then((docs) => setSavedProjects(docs))
  }, [])

  // Open a fresh chat session seeded with the given artifact — same code
  // path as opening a pin from the scrapbook, just sourced from a
  // chat-inline ZoomableViewer's "New project" CTA instead.
  const handleNewProjectFromArtifact = useCallback((artifact: Artifact) => {
    setCurrentDocId(null)
    onLoadDoc?.(loomDocFromArtifact(artifact))
  }, [onLoadDoc])

  // ── File operations ──────────────────────────────────────────────────────────

  const handleNewProject = useCallback(() => {
    setSubmenuStack([])
    setCurrentDocId(null)
    onReset?.()
  }, [onReset])

  const handleSaveProject = useCallback(async () => {
    const existingId = getCurrentDocId()
    const now = new Date().toISOString()
    const id = existingId ?? crypto.randomUUID()
    const projectDoc: ProjectDoc = {
      id,
      name: doc.project.name,
      createdAt: existingId ? (await loadProjectDoc(id))?.createdAt ?? now : now,
      updatedAt: now,
      doc,
    }
    await saveProjectDoc(projectDoc)
    setCurrentDocId(id)
    await refreshProjects()
    setSubmenuStack([])
  }, [doc, refreshProjects])

  const handleOpenProject = useCallback(async (id: string) => {
    const projectDoc = await loadProjectDoc(id)
    if (!projectDoc) return
    setCurrentDocId(id)
    setSubmenuStack([])
    onLoadDoc?.(projectDoc.doc)
  }, [onLoadDoc])

  const handleExportProject = useCallback(() => {
    const exported = JSON.stringify(doc, null, 2)
    const blob = new Blob([exported], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${doc.project.name.replace(/\s+/g, "-").toLowerCase()}.json`
    a.click()
    URL.revokeObjectURL(url)
    setSubmenuStack([])
  }, [doc])

  const handleImportProject = useCallback(() => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = ".json"
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      try {
        const text = await file.text()
        const data = JSON.parse(text) as Partial<LoomDoc> & { project?: Project; transcript?: UIMessage[] }
        // Accept either the new full-doc shape or the legacy { project, transcript } export.
        if (!data.project) return
        const incoming: LoomDoc = {
          project: fromDict(data.project as unknown as Record<string, unknown>),
          transcript: data.transcript ?? [],
          toolOutputs: data.toolOutputs ?? EMPTY_TOOL_OUTPUTS,
          schemaVersion: data.schemaVersion ?? 1,
          phase: data.phase ?? null,
        }
        setCurrentDocId(null)
        setSubmenuStack([])
        onLoadDoc?.(incoming)
      } catch {
        // silently ignore malformed files
      }
    }
    input.click()
  }, [onLoadDoc])

  const { state: voiceState, start: startVoice, stop: stopVoice, supported: voiceSupported } =
    useVoiceInput((tx) => {
      setInput((prev) => (prev ? `${prev} ${tx}` : tx))
      textareaRef.current?.focus()
    })

  const canMakePdf = project.fabrics.length > 0 && project.layout.rows > 0 && project.layout.cols > 0

  const handleDownloadPdf = useCallback(async () => {
    const { generatePdfBlob } = await import("@/lib/pdf")
    const blob = await generatePdfBlob(project)
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${project.name.replace(/\s+/g, "-").toLowerCase()}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }, [project])

  const isLoading = status === "streaming" || status === "submitted"
  const isFreshConversation = messages.length === 0
  // Returns false during SSR and client hydration so both sides agree on an
  // empty seed list; switches to true after hydration so the client shows the
  // randomly-picked seeds without ever mismatching the server HTML.
  const isMounted = useSyncExternalStore(() => () => {}, () => true, () => false)
  // Pre-pick seeds per phase once at mount so switching tabs shows different
  // suggestions without re-rolling on every render. Each phase's pool =
  // (seeds tagged with that phase) ∪ (untagged ambient seeds).
  const [seedsByPhase] = useState<Record<Phase, MenuOption[]>>(() => {
    const result = {} as Record<Phase, MenuOption[]>
    for (const ph of PHASES) {
      const pool = SEED_POOL.filter((s) => !s.phase || s.phase === ph)
      result[ph] = pickRandom(pool, 7)
    }
    return result
  })
  const seedPick = isMounted ? seedsByPhase[activePhase] : []
  const menuOptions: MenuOption[] =
    pendingOptions.length > 0
      ? pendingOptions
      : isFreshConversation
      ? seedPick
      : []
  // Phase-scoped dimension rows. Filtering by phase keeps the menu focused
  // on the current kind of work; per-dimension submenus (instead of the
  // previous category-flatten) keep each dimension's values together. Shown
  // on fresh conversations too so the Blocks tab surfaces the full
  // block-style submenu without requiring a tool call first.
  const dimensionRows: MenuOption[] = !isLoading
    ? dimensionMenuRows(dimensionsForPhase(activePhase), project)
    : []

  // Fabrics tab has no dimensions in the DIMENSIONS table (continuous values
  // don't fit the registry). Inject the Colorway submenu here so the tab
  // isn't barren mid-journey — same shape REC_002 calls out.
  const colorwayRows: MenuOption[] = !isLoading && activePhase === "fabrics"
    ? colorwayMenuRows(project)
    : []

  // ── File menu (always visible) ───────────────────────────────────────────────

  // Project switcher icon hints at whether each saved doc started life as
  // a block scratchpad (from a block pin) or a finished quilt design.
  const openProjectItems: MenuOption[] = savedProjects.map((projectDoc) => ({
    text: projectDoc.name,
    icon: deriveDocKind(projectDoc) === "block" ? "block" : "quilt",
    onSelect: () => handleOpenProject(projectDoc.id),
  }))

  const fileMenuChildren: MenuOption[] = [
    { text: "New project", icon: "add", onSelect: handleNewProject },
    { text: "Save", icon: "edit", onSelect: handleSaveProject },
    ...(openProjectItems.length > 0
      ? [{ text: "Open", icon: "plan" as const, children: openProjectItems }]
      : []),
    { text: "Export JSON", icon: "next", onSelect: handleExportProject },
    { text: "Import JSON", icon: "fabric", onSelect: handleImportProject },
  ]

  const handleMenuItemClick = (item: MenuOption) => {
    if (item.disabledReason) return
    if (item.children) {
      openSubmenu(item.text, item.children)
    } else if (item.synth) {
      synthesize(item.synth)
    } else if (item.onSelect) {
      item.onSelect()
    } else {
      handleSendText(item.text)
    }
  }

  const currentSubmenu = submenuStack.at(-1)
  const activeMenuItems: MenuOption[] = currentSubmenu
    ? currentSubmenu.items
    : [
        { text: "File", icon: "plan", children: fileMenuChildren },
        ...menuOptions,
        ...dimensionRows,
        ...colorwayRows,
      ]

  // Shared base for every row in the action panel. active: gives touch
  // feedback on mobile where hover never fires.
  const menuButtonClass = "flex w-full items-center gap-2.5 bg-background hover:bg-muted active:bg-muted px-3 py-2 text-2xl text-left transition-colors"

  return (
    <div className="h-full overflow-y-auto">
      <div className="flex flex-col p-3 max-w-2xl mx-auto">
        <div className="flex-1 space-y-2">
          {isFreshConversation && (
            <div className="text-2xl text-foreground animate-fade-in">{WELCOME}</div>
          )}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`text-2xl animate-fade-in ${message.role === "user" ? "italic text-muted-foreground" : "text-foreground"}`}
            >
              {message.parts.map((part, i) => {
                if (isTextUIPart(part)) {
                  if (message.role === "assistant") {
                    return <Markdown key={i} text={part.text} />
                  }
                  return (
                    <span key={i} className="whitespace-pre-wrap">
                      {part.text}
                    </span>
                  )
                }
                if (part.type === "dynamic-tool") {
                  return (
                    <ToolVisual
                      key={part.toolCallId}
                      toolName={part.toolName}
                      state={part.state}
                      output={part.state === "output-available" ? part.output : undefined}
                      block={blockSvgs[part.toolCallId]}
                      diagram={diagramSvgs[part.toolCallId]}
                      quilt={quiltSvgs[part.toolCallId]}
                      quiltGrid={quiltGrids[part.toolCallId]}
                      planMarkdown={planMarkdowns[part.toolCallId]}
                      pinnedIds={pinnedIds}
                      onTogglePin={onTogglePin}
                      onNewProjectFromArtifact={handleNewProjectFromArtifact}
                      onColorChange={handleColorChange}
                    />
                  )
                }
                return null
              })}
            </div>
          ))}
          {isLoading && <SewingIndicator />}

        </div>

        <div className="mt-2 border-t border-border pt-2">
          {/* Phase strip — the LCD readout / mode chooser. Sits above the
              menu rows so the user can flip phases and the menu retargets. */}
          <div className="mb-1.5">
            <PhaseStrip active={activePhase} onSelect={setPhase} />
          </div>

          {/* role="group" with aria-label — these are real <button>s reached
              by Tab. role="menu"/"menuitem" would promise arrow-key navigation
              we don't implement. */}
          <div
            role="group"
            aria-label="Quilt actions"
            className="mb-1.5 flex flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-background animate-fade-in"
          >
            {/* Sub-menu back navigation */}
            {currentSubmenu && (
              <button
                onClick={closeSubmenu}
                className={`${menuButtonClass} text-muted-foreground`}
              >
                <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">
                  <ChevronLeft size={16} strokeWidth={1.75} />
                </span>
                <span className="flex-1 truncate font-medium">{currentSubmenu.title}</span>
              </button>
            )}

            {/* PDF download (only in root menu) */}
            {!currentSubmenu && canMakePdf && !isLoading && (
              <button
                onClick={handleDownloadPdf}
                className={menuButtonClass}
              >
                <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 text-muted-foreground">
                  <Download size={16} strokeWidth={1.75} className="text-muted-foreground" />
                </span>
                <span className="flex-1 truncate">Download PDF</span>
              </button>
            )}

            {/* Active menu items (root or sub-menu) */}
            {activeMenuItems.map((s, i) => {
              const iconNode = s.icon ? (
                <MenuIcon name={s.icon} />
              ) : s.swatches && s.swatches.length > 0 ? (
                <span className="flex -space-x-1">
                  {s.swatches.map((c, j) => (
                    <span
                      key={j}
                      className="w-2.5 h-2.5 rounded-full border border-black/15"
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </span>
              ) : null
              // Color-picker row: wrap a hidden <input type="color"> in a
              // <label> so the row tap opens the native picker. Same primitive
              // BlockColorSwatches uses; can't sit inside a <button>, so this
              // row is a <label>. On `change` we reuse handleColorChange — the
              // exact LOOM_004 chain (colorway_set + render_block).
              if (s.colorPicker) {
                const { slot, current } = s.colorPicker
                return (
                  <label
                    key={`${i}-${s.text}`}
                    className={`${menuButtonClass} cursor-pointer ${isLoading ? "opacity-50 pointer-events-none" : ""}`}
                    title={`Change ${slot} color`}
                  >
                    <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 text-muted-foreground">
                      {iconNode}
                    </span>
                    <span className="flex-1 truncate">{s.text}</span>
                    <input
                      type="color"
                      value={current}
                      onChange={(e) => handleColorChange(slot, e.target.value)}
                      className="sr-only"
                      aria-label={`Change ${slot} color`}
                    />
                  </label>
                )
              }
              const isLockedDisabled = !!s.disabledReason
              return (
                <button
                  key={`${i}-${s.text}`}
                  onClick={() => handleMenuItemClick(s)}
                  disabled={isLockedDisabled || (isLoading && !s.onSelect && !s.children && !s.synth)}
                  title={s.disabledReason}
                  className={`${menuButtonClass} disabled:opacity-50`}
                >
                  <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 text-muted-foreground">
                    {iconNode}
                  </span>
                  <span className={`flex-1 ${s.disabledReason ? "" : "truncate"}`}>
                    {s.text}
                    {s.disabledReason && (
                      <span className="ml-2 text-base italic text-muted-foreground">
                        — {s.disabledReason}
                      </span>
                    )}
                  </span>
                  {s.children && !isLockedDisabled && (
                    <span className="flex-shrink-0 text-muted-foreground">
                      <ChevronRight size={14} strokeWidth={1.75} />
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className="flex gap-2 items-end">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              placeholder={isLoading ? "Working…" : "What next?"}
              rows={1}
              className="flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-2xl placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring min-h-[44px] max-h-32 overflow-y-auto field-sizing-content disabled:opacity-40"
            />
            {voiceSupported && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <IconButton
                      onClick={voiceState === "listening" ? stopVoice : voiceState === "denied" ? undefined : startVoice}
                      disabled={voiceState === "denied"}
                      aria-label={
                        voiceState === "listening" ? "Stop listening"
                        : voiceState === "denied" ? "Microphone access denied"
                        : "Start voice input"
                      }
                      className={`border transition-colors ${
                        voiceState === "listening"
                          ? "border-destructive bg-destructive/10 text-destructive"
                          : voiceState === "denied"
                          ? "border-border text-muted-foreground opacity-40 cursor-not-allowed"
                          : "border-border hover:bg-muted text-muted-foreground"
                      }`}
                    />
                  }
                >
                  <MicIcon active={voiceState === "listening"} denied={voiceState === "denied"} />
                </TooltipTrigger>
                <TooltipContent>
                  {voiceState === "listening" ? "Stop listening"
                   : voiceState === "denied" ? "Microphone access denied — check browser settings"
                   : "Voice input"}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger
                render={
                  <IconButton
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    aria-label="Send message"
                    className="bg-foreground text-background transition-opacity"
                  />
                }
              >
                <SendIcon />
              </TooltipTrigger>
              <TooltipContent>Send message</TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div ref={messagesEndRef} />
      </div>
    </div>
  )
}

function SewingIndicator() {
  return (
    <div
      aria-label="Working"
      className="flex items-center gap-2 text-muted-foreground animate-fade-in py-1"
    >
      <svg
        width="8" height="24" viewBox="0 0 8 24"
        fill="none" aria-hidden="true"
        className="sewing-needle flex-shrink-0"
      >
        <rect x="1" y="0" width="6" height="18" rx="3" fill="currentColor" />
        <polygon points="1,16 7,16 4,23" fill="currentColor" />
        <ellipse cx="4" cy="9" rx="1.4" ry="1.8"
          fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="0.8" />
      </svg>
      <div className="flex-1 relative h-px">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "repeating-linear-gradient(to right, currentColor 0, currentColor 4px, transparent 4px, transparent 7px)" }}
        />
        <div className="absolute left-0 top-0 h-full bg-current opacity-60 sewing-seam-grow" />
      </div>
    </div>
  )
}

// Memoized so prior assistant-message renders skip ReactMarkdown re-parsing
// while the latest message streams in token-by-token (LOOM_017). The single
// `text` prop is a primitive — default shallow equality is sufficient.
const Markdown = memo(function Markdown({ text }: { text: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        ul: ({ children }) => <ul className="ml-4 list-disc space-y-0.5 mb-1">{children}</ul>,
        ol: ({ children }) => <ol className="ml-4 list-decimal space-y-0.5 mb-1">{children}</ol>,
        li: ({ children }) => <li className="leading-normal">{children}</li>,
        code: ({ children }) => (
          <code className="rounded bg-black/10 px-1 py-0.5 font-mono text-xl">{children}</code>
        ),
        pre: ({ children }) => (
          <pre className="rounded bg-black/10 p-2 font-mono text-xl overflow-x-auto my-1 whitespace-pre-wrap">{children}</pre>
        ),
        h1: ({ children }) => <h1 className="text-3xl font-bold mt-3 mb-1 first:mt-0">{children}</h1>,
        h2: ({ children }) => <h2 className="text-3xl font-semibold mt-3 mb-1 first:mt-0">{children}</h2>,
        h3: ({ children }) => <h3 className="text-2xl font-semibold mt-2 mb-0.5 first:mt-0">{children}</h3>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-1">
            <table className="text-xl border-collapse">{children}</table>
          </div>
        ),
        th: ({ children }) => <th className="border border-black/20 px-2 py-0.5 font-semibold bg-black/5">{children}</th>,
        td: ({ children }) => <td className="border border-black/20 px-2 py-0.5">{children}</td>,
      }}
    >
      {text}
    </ReactMarkdown>
  )
})

// ── Action labels and visual outputs ─────────────────────────────────────────

const TOOL_LABELS: Record<Exclude<ToolName, "show_menu">, string> = {
  project_set:          "Naming the quilt",
  fabric_add:           "Recording fabric",
  fabric_update:        "Updating fabric",
  fabric_remove:        "Removing fabric",
  fabric_list:          "Reading fabrics",
  block_set:            "Configuring block",
  block_info:           "Reading block",
  layout_set:           "Setting layout",
  colorway_set:         "Setting palette",
  colorway_list:        "Reading palettes",
  capacity:             "Checking capacity",
  analyze:              "Checking yardage",
  render_block:         "Drawing block",
  render_block_diagram: "Labelling block",
  render_quilt:         "Drawing the quilt",
  render_quilt_grid:    "Drawing quilt variations",
  render_plan:          "Building cut plan",
  sandwich_plan:        "Computing sandwich needs",
  sashing_set:          "Configuring sashing",
  cell_set:             "Placing block",
  value_audit:          "Checking contrast",
}

// PinOverlay — bridges an Artifact to PinToggle by computing the artifact's
// content-hash ID, looking up its pinned status in the live set, and routing
// click events to the parent's toggle handler. Returns null until the hash
// resolves (one async tick) so the pin button doesn't briefly render in the
// wrong state. The wrapping div in each render point provides the
// `relative group` positioning context the toggle's absolute layout needs.
// One swatch per slot in the active block — taps open a native color picker
// and dispatch colorway_set + render_block via the synthesis chain. No prose,
// no AI round-trip; the block re-renders with the new color in the transcript
// below. Solves LOOM_004's "I can't recolor without typing" gap.
function BlockColorSwatches({ colorway, onChange }: {
  colorway: Record<string, string>
  onChange: (slot: string, hex: string) => void
}) {
  // Track per-slot working color so the swatch shows the user's drag in real
  // time. We only commit (synthesize) on `change` (picker close), not `input`
  // (drag tick) — every commit appends a new render, so dragging would flood
  // the transcript otherwise.
  const [working, setWorking] = useState<Record<string, string>>({})
  const slots = Object.keys(colorway)
  if (slots.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {slots.map((slot) => {
        const current = working[slot] ?? colorway[slot]
        return (
          <label
            key={slot}
            className="flex items-center gap-1.5 text-base text-muted-foreground cursor-pointer"
            title={`Change ${slot} color`}
          >
            <span
              className="inline-block w-6 h-6 rounded-full border border-black/15 shadow-sm"
              style={{ backgroundColor: current }}
              aria-hidden="true"
            />
            <input
              type="color"
              value={current}
              onInput={(e) => setWorking((w) => ({ ...w, [slot]: (e.target as HTMLInputElement).value }))}
              onChange={(e) => {
                const hex = e.target.value
                setWorking((w) => ({ ...w, [slot]: hex }))
                onChange(slot, hex)
              }}
              className="sr-only"
              aria-label={`Change ${slot} color`}
            />
            <span className="capitalize">{slot}</span>
          </label>
        )
      })}
    </div>
  )
}

function PinOverlay({ artifact, pinnedIds, onToggle, alwaysVisible, layout }: {
  artifact: Artifact
  pinnedIds: Set<string>
  onToggle: (artifact: Artifact, id: string) => void
  alwaysVisible?: boolean
  layout?: "overlay" | "free"
}) {
  const [id, setId] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    pinIdFor(artifact).then((computed) => { if (!cancelled) setId(computed) })
    return () => { cancelled = true }
  }, [artifact])
  if (!id) return null
  return (
    <PinToggle
      pinned={pinnedIds.has(id)}
      onToggle={() => onToggle(artifact, id)}
      alwaysVisible={alwaysVisible}
      layout={layout}
    />
  )
}

interface ToolVisualProps {
  toolName: string
  state: string
  output: unknown
  block?: RenderedBlock
  diagram?: RenderedBlock
  quilt?: RenderedQuilt
  quiltGrid?: RenderedThumbnail[]
  planMarkdown?: string
  pinnedIds: Set<string>
  onTogglePin: (artifact: Artifact, id: string) => void
  onNewProjectFromArtifact: (artifact: Artifact) => void
  onColorChange?: (slot: string, hex: string) => void
}

// Custom equality so ToolVisual skips re-rendering during streaming chunks
// when only the in-flight assistant text changed (LOOM_017). Every prop except
// `output` is a stable reference across renders; `output` can be rebuilt by
// the AI SDK even when content is unchanged, so we fall back to a stringify
// compare. Outputs are at most a few KB.
function toolVisualPropsEqual(prev: ToolVisualProps, next: ToolVisualProps): boolean {
  if (
    prev.toolName !== next.toolName ||
    prev.state !== next.state ||
    prev.block !== next.block ||
    prev.diagram !== next.diagram ||
    prev.quilt !== next.quilt ||
    prev.quiltGrid !== next.quiltGrid ||
    prev.planMarkdown !== next.planMarkdown ||
    prev.pinnedIds !== next.pinnedIds ||
    prev.onTogglePin !== next.onTogglePin ||
    prev.onNewProjectFromArtifact !== next.onNewProjectFromArtifact ||
    prev.onColorChange !== next.onColorChange
  ) return false
  if (prev.output === next.output) return true
  return JSON.stringify(prev.output) === JSON.stringify(next.output)
}

const ToolVisual = memo(function ToolVisual({ toolName, state, output, block, diagram, quilt, quiltGrid, planMarkdown, pinnedIds, onTogglePin, onNewProjectFromArtifact, onColorChange }: ToolVisualProps) {
  const [zoomSvg, setZoomSvg] = useState<{ svg: string; label: string; artifact: Artifact } | null>(null)
  // Block-preview <img> uses an SVG data URL. encodeURIComponent on a multi-KB
  // SVG runs every render; cache it on block.svg so streaming re-renders of
  // ChatPane don't re-encode (LOOM_017). Hook called before any early return.
  const blockDataUrl = useMemo(
    () => block ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(block.svg)}` : "",
    [block],
  )
  if (toolName === "show_menu") return null
  const label = (toolName in TOOL_LABELS) ? TOOL_LABELS[toolName as keyof typeof TOOL_LABELS] : toolName
  const isComplete = state === "output-available"
  const o = (isComplete && output && typeof output === "object") ? output as Record<string, unknown> : null

  const colorsMap = (toolName === "colorway_set" && o?.colors && typeof o.colors === "object")
    ? o.colors as Record<string, string> : null
  const colorwayGroup = (toolName === "colorway_set" && typeof o?.group === "string")
    ? o.group as string : null
  const colorways = (toolName === "colorway_list" && o?.colorways && typeof o.colorways === "object")
    ? o.colorways as Record<string, Record<string, string>> : null
  const usages = (toolName === "analyze" && Array.isArray(o?.usages))
    ? o!.usages as UsageRow[] : null
  const fabrics = (toolName === "fabric_list" && Array.isArray(o?.fabrics))
    ? o!.fabrics as FabricRow[] : null
  const capacity = (toolName === "capacity" && o?.maxTotal != null)
    ? o as unknown as CapacityReport : null
  const addedFabric = ((toolName === "fabric_add" || toolName === "fabric_update") && o?.fabric && typeof o.fabric === "object")
    ? o.fabric as FabricRow : null
  const projectName = (toolName === "project_set" && typeof o?.name === "string")
    ? o.name as string : null
  const blockOut = (toolName === "block_set" && o?.block && typeof o.block === "object")
    ? o.block as BlockOut : null
  const blockFinished = (toolName === "block_set" && Array.isArray(o?.finished_inches))
    ? o!.finished_inches as [number, number] : null
  const blockStyleLabel = (toolName === "block_set" && typeof o?.style_label === "string")
    ? o!.style_label as string : null
  const layoutOut = (toolName === "layout_set" && o?.layout && typeof o.layout === "object")
    ? o.layout as LayoutOut : null
  const blockInfo = (toolName === "block_info" && Array.isArray(o?.pieces))
    ? o as unknown as BlockInfoOut : null
  const sandwichOut = (toolName === "sandwich_plan" && typeof (o as Record<string, unknown>)?.bindingYards === "number")
    ? o as unknown as SandwichPlan : null
  const cellOut = (toolName === "cell_set" && o?.ok === true && typeof o?.rows === "number")
    ? o as unknown as CellOut : null

  return (
    <div className="mt-1.5 text-xl text-muted-foreground animate-affordance-in">
      <span className="italic">{label}{!isComplete ? "…" : ""}</span>

      {/* Block SVG preview */}
      {!!block && (
        <div className="mt-2 w-full max-w-xs">
          <div className="relative group">
            <button
              type="button"
              onClick={() => setZoomSvg({ svg: block.svg, artifact: block.artifact, label: "Block preview" })}
              aria-label="Open block in full-screen view"
              className="block w-full overflow-hidden rounded-lg border border-black/10 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <img
                src={blockDataUrl}
                alt="Block preview"
                className="w-full h-auto block rounded-lg"
              />
            </button>
            <PinOverlay artifact={block.artifact} pinnedIds={pinnedIds} onToggle={onTogglePin} />
          </div>
          {/* Per-slot swatch row: tap to recolor the block without typing.
              Each swatch synthesizes colorway_set + render_block in one
              transcript event (see handleColorChange in ChatPane). */}
          {onColorChange && (
            <BlockColorSwatches colorway={block.artifact.colorway} onChange={onColorChange} />
          )}
        </div>
      )}

      {/* Block diagram (labelled) */}
      {!!diagram && (
        <div className="relative group mt-2 w-full">
          <button
            type="button"
            onClick={() => setZoomSvg({ svg: diagram.svg, artifact: diagram.artifact, label: "Block diagram" })}
            aria-label="Open block diagram in full-screen view"
            className="block w-full overflow-hidden rounded-lg border border-black/10 bg-background cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-ring [&>svg]:block [&>svg]:w-full [&>svg]:h-auto"
            dangerouslySetInnerHTML={{ __html: diagram.svg }}
          />
          <PinOverlay artifact={diagram.artifact} pinnedIds={pinnedIds} onToggle={onTogglePin} />
        </div>
      )}

      {/* Full quilt SVG preview */}
      {!!quilt && (
        <div className="relative group mt-2 w-full">
          <button
            type="button"
            onClick={() => setZoomSvg({ svg: quilt.svg, artifact: quilt.artifact, label: "Full quilt preview" })}
            aria-label="Open quilt in full-screen view"
            className="block w-full overflow-hidden rounded-lg border border-black/10 bg-background cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-ring [&>svg]:block [&>svg]:w-full [&>svg]:h-auto"
            dangerouslySetInnerHTML={{ __html: quilt.svg }}
          />
          <PinOverlay artifact={quilt.artifact} pinnedIds={pinnedIds} onToggle={onTogglePin} />
        </div>
      )}

      {/* Quilt grid — thumbnails varying along one dimension.
          Container caps height (max-h) and exposes horizontal + vertical
          scrollbars: the inner row is nowrap so it grows as wide as the
          content needs; max-h bounds the visible height. Each thumbnail
          carries its own artifact (different variant of the project) — pin
          state is per-thumbnail. */}
      {quiltGrid && quiltGrid.length > 0 && (
        <div className="mt-2 max-h-[480px] overflow-auto rounded-lg border border-black/10 bg-black/[.02] p-2">
          <div className="flex flex-nowrap gap-2 w-max">
            {quiltGrid.map((item, idx) => (
              <div key={idx} className="relative group flex-shrink-0 w-40">
                <button
                  type="button"
                  onClick={() => setZoomSvg({ svg: item.svg, artifact: item.artifact, label: item.label })}
                  aria-label={`Open ${item.label} in full-screen view`}
                  className="w-full rounded-lg border border-black/10 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-ring hover:shadow-md transition-shadow overflow-hidden bg-background"
                >
                  <div
                    className="block [&>svg]:block [&>svg]:w-full [&>svg]:h-auto"
                    dangerouslySetInnerHTML={{ __html: item.svg }}
                  />
                  <div className="text-xs font-medium px-1.5 py-1 text-center text-foreground bg-black/[.03]">
                    {item.label}
                  </div>
                </button>
                <PinOverlay artifact={item.artifact} pinnedIds={pinnedIds} onToggle={onTogglePin} />
              </div>
            ))}
          </div>
        </div>
      )}

      {zoomSvg && (
        <ZoomableViewer
          svg={zoomSvg.svg}
          ariaLabel={zoomSvg.label}
          onClose={() => setZoomSvg(null)}
          pinOverlay={
            <PinOverlay
              artifact={zoomSvg.artifact}
              pinnedIds={pinnedIds}
              onToggle={onTogglePin}
              layout="free"
              alwaysVisible
            />
          }
          onNewProject={() => {
            onNewProjectFromArtifact(zoomSvg.artifact)
            setZoomSvg(null)
          }}
          newProjectLabel={`New project from this ${zoomSvg.artifact.kind}`}
        />
      )}

      {/* Cut plan — full markdown */}
      {!!planMarkdown && (
        <div className="mt-2 border border-black/10 rounded-lg p-3 bg-black/[.02] text-foreground">
          <Markdown text={planMarkdown} />
        </div>
      )}

      {/* Sandwich plan — binding / backing / batting summary */}
      {sandwichOut != null && (
        <div className="mt-2 space-y-1.5 text-sm">
          <div className="opacity-60 text-xs">
            Quilt top {inFraction(sandwichOut.quiltW)} × {inFraction(sandwichOut.quiltH)}
          </div>
          <div className="flex gap-2">
            <span className="opacity-50 w-16 flex-shrink-0">Binding</span>
            <span className="font-medium text-foreground">{sandwichOut.bindingYards} yd</span>
            <span className="opacity-50 text-xs">{sandwichOut.bindingStripCount} strips × 2½&quot; WOF</span>
          </div>
          <div className="flex gap-2">
            <span className="opacity-50 w-16 flex-shrink-0">Backing</span>
            <span className="font-medium text-foreground">{sandwichOut.backingYards} yd</span>
            <span className="opacity-50 text-xs">{sandwichOut.backingPanels === 2 ? "2 panels" : "1 panel"}</span>
          </div>
          {sandwichOut.backingNote && (
            <div className="opacity-60 text-xs italic">{sandwichOut.backingNote}</div>
          )}
          <div className="flex gap-2">
            <span className="opacity-50 w-16 flex-shrink-0">Batting</span>
            {sandwichOut.battingSize
              ? <span className="font-medium text-foreground">{sandwichOut.battingSize}</span>
              : <span className="text-warning">custom size needed</span>}
          </div>
        </div>
      )}

      {/* Fabric list — swatch with quantity-scaled width per row */}
      {fabrics != null && fabrics.length > 0 && (() => {
        const maxYards = Math.max(...fabrics.map((f) => f.yards), 1)
        return (
          <div className="mt-2 flex flex-col gap-1">
            {fabrics.map((f) => (
              <div key={f.name} className="flex items-center gap-2">
                <span
                  className="h-4 rounded-sm border border-black/15 flex-shrink-0"
                  style={{
                    backgroundColor: f.swatch ?? "#888",
                    width: `${Math.max(8, (f.yards / maxYards) * 64)}px`,
                  }}
                  aria-label={`${f.yards} yards`}
                />
                <span className="font-medium text-foreground truncate">{f.name}</span>
                <span className="opacity-60 ml-auto whitespace-nowrap">
                  {f.role ?? f.slot ?? "—"}{f.group ? ` · ${f.group}` : ""} · {f.yards} yd
                </span>
              </div>
            ))}
          </div>
        )
      })()}

      {/* Capacity — max blocks + layout options */}
      {capacity != null && (
        <div className="mt-1.5 space-y-1">
          <div className="opacity-80">
            Max <span className="font-medium text-foreground">{capacity.maxTotal}</span> blocks
            · limited by <span className="font-medium text-foreground">{capacity.constraining.fabric}</span>
          </div>
          {capacity.singleLayouts.length > 0 && (
            <table className="border-collapse">
              <thead>
                <tr className="opacity-50">
                  <th className="text-left font-medium pr-4 pb-0.5">grid</th>
                  <th className="text-left font-medium pb-0.5">finished size</th>
                </tr>
              </thead>
              <tbody>
                {capacity.singleLayouts.slice(0, 6).map(([big, small]) => {
                  const cols = small, rows = big
                  const w = (cols * capacity.blockWidthInches).toFixed(0)
                  const h = (rows * capacity.blockHeightInches).toFixed(0)
                  return (
                    <tr key={`${big}x${small}`} className="border-t border-black/5">
                      <td className="pr-4 py-0.5">{cols} × {rows}</td>
                      <td className="py-0.5 opacity-70">{w}&quot; × {h}&quot;</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Colorway swatches — slots come from the colorway map itself, which is
          keyed by the active schema's slots (schemaSlots) in schema order. */}
      {colorsMap != null && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          {colorwayGroup && (
            <span className="font-medium text-foreground">Group {colorwayGroup}</span>
          )}
          {Object.entries(colorsMap).map(([role, c]) => (
            <div key={role} className="flex items-center gap-1">
              <div className="w-4 h-4 rounded border border-black/10 flex-shrink-0" style={{ backgroundColor: c }} title={role} />
              <span className="opacity-60">{role}</span>
            </div>
          ))}
        </div>
      )}

      {colorways != null && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {Object.entries(colorways).map(([g, c]) => (
            <div key={g} className="flex items-center gap-2">
              <span className="opacity-50 w-4">{g}</span>
              <div className="flex gap-1">
                {Object.entries(c).map(([role, color]) => (
                  <div key={role} className="w-4 h-4 rounded border border-black/10"
                    style={{ backgroundColor: color }} title={role} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Yardage budget bars */}
      {usages != null && (
        <div className="mt-1.5 space-y-1.5">
          {usages.map((u) => (
            <div key={u.fabric} className="space-y-0.5">
              <div className="opacity-80 truncate">{u.fabric}</div>
              <BudgetBar needInches={u.need_inches} haveInches={u.have_inches} label={u.fabric} />
            </div>
          ))}
        </div>
      )}

      {/* fabric_add / fabric_update — swatch tile + name + role/group/yards */}
      {addedFabric != null && (
        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
          <div
            className="w-10 h-10 rounded-md border border-black/15 flex-shrink-0 shadow-sm"
            style={{ backgroundColor: addedFabric.swatch ?? "#888" }}
            aria-label={`${addedFabric.name} swatch`}
          />
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-medium text-foreground truncate">{addedFabric.name}</span>
            <span className="opacity-60 whitespace-nowrap">
              {addedFabric.role ?? addedFabric.slot ?? "—"}
              {addedFabric.group ? ` · group ${addedFabric.group}` : ""}
              {" · "}
              <span className="text-foreground">{addedFabric.yards} yd</span>
            </span>
          </div>
          <YardageBar yards={addedFabric.yards} />
        </div>
      )}

      {/* project_set — show the new name in title style */}
      {projectName != null && (
        <div className="mt-1.5">
          <span className="font-display text-2xl text-foreground">&ldquo;{projectName}&rdquo;</span>
        </div>
      )}

      {/* block_set — unit/seam/finished + schema-driven block preview */}
      {blockOut != null && (
        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
          <BlockRolesDiagram block={blockOut} />
          <div className="flex flex-col gap-0.5">
            <div>
              <span className="font-medium text-foreground">{blockOut.unit_inches}&quot;</span>
              <span className="opacity-60"> unit · </span>
              <span className="font-medium text-foreground">
                {blockFinished != null
                  ? blockFinished[0] === blockFinished[1]
                    ? `${inFraction(blockFinished[0])}`
                    : `${inFraction(blockFinished[0])} × ${inFraction(blockFinished[1])}`
                  : ""}
              </span>
              <span className="opacity-60"> finished</span>
            </div>
            <div className="opacity-60">
              {blockOut.seam}&quot; seam
              {blockStyleLabel ? ` · ${blockStyleLabel}` : blockOut.style ? ` · ${blockOut.style}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* block_info — per-piece cut breakdown */}
      {blockInfo != null && (
        <div className="mt-2 flex flex-col gap-1.5">
          <div className="opacity-80">
            <span className="font-medium text-foreground">{blockInfo.style.replace(/_/g, " ")}</span>
            <span className="opacity-60"> · </span>
            <span className="font-medium text-foreground">{inFraction(blockInfo.finished_inches[0])}{blockInfo.finished_inches[0] !== blockInfo.finished_inches[1] ? `×${inFraction(blockInfo.finished_inches[1])}` : ""}</span>
            <span className="opacity-60"> finished · </span>
            <span className="font-medium text-foreground">{blockInfo.total_blocks}</span>
            <span className="opacity-60"> blocks{blockInfo.groups > 0 ? ` · ${blockInfo.blocks_per_group}/group` : ""}</span>
            {blockInfo.family && <>
              <span className="opacity-60"> · </span>
              <span className="opacity-60">{blockInfo.family}</span>
            </>}
          </div>
          <table className="border-collapse text-foreground">
            <thead>
              <tr className="opacity-50">
                <th className="text-left font-medium pr-3 pb-0.5">piece</th>
                <th className="text-right font-medium pr-3 pb-0.5">cut</th>
                <th className="text-right font-medium pr-3 pb-0.5">×blk</th>
                <th className="text-right font-medium pr-3 pb-0.5">total</th>
                <th className="text-right font-medium pr-3 pb-0.5">strips</th>
                <th className="text-left font-medium pb-0.5">fabric</th>
              </tr>
            </thead>
            <tbody>
              {blockInfo.pieces.map((p) => (
                <tr key={p.name} className="border-t border-black/5 align-top">
                  <td className="pr-3 py-0.5">{p.name.replace(/_/g, " ")}</td>
                  <td className="pr-3 py-0.5 text-right tabular-nums">
                    {p.cut_size === p.cut_length
                      ? `${inFraction(p.cut_size)} sq`
                      : `${inFraction(p.cut_size)} × ${inFraction(p.cut_length)}`}
                  </td>
                  <td className="pr-3 py-0.5 text-right tabular-nums opacity-70">{p.count_per_block}</td>
                  <td className="pr-3 py-0.5 text-right tabular-nums">{p.total_count}</td>
                  <td className="pr-3 py-0.5 text-right tabular-nums opacity-70">
                    {p.strip_plan != null ? p.strip_plan.strips_per_group : "—"}
                  </td>
                  <td className="py-0.5">
                    <span className="opacity-80">{p.fabric_slot}</span>
                    {p.fabric_by_group && Object.keys(p.fabric_by_group).length > 0 && (
                      <span className="opacity-50 ml-1 text-xs">
                        {Object.entries(p.fabric_by_group).map(([g, n]) => `${g}→${n}`).join(", ")}
                      </span>
                    )}
                    {p.missing_groups.length > 0 && (
                      <span className="ml-1 text-destructive">missing {p.missing_groups.join(", ")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* layout_set — grid summary + dot grid */}
      {layoutOut != null && (
        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
          <LayoutDotGrid rows={layoutOut.rows} cols={layoutOut.cols} />
          <div className="flex flex-col gap-0.5">
            <div>
              <span className="font-medium text-foreground">{layoutOut.cols} × {layoutOut.rows}</span>
              <span className="opacity-60"> = </span>
              <span className="font-medium text-foreground">{layoutOut.rows * layoutOut.cols}</span>
              <span className="opacity-60"> blocks</span>
            </div>
            <div className="opacity-60">
              {distributionLabel(layoutOut.distribution)}
              {layoutOut.sashing_width && layoutOut.sashing_width > 0 ? ` · ${layoutOut.sashing_width}" sashing` : ""}
            </div>
          </div>
        </div>
      )}

      {/* cell_set — mini grid with override cells highlighted */}
      {cellOut != null && (() => {
        const { r, c, style, rows, cols, cell_schemas } = cellOut
        const cellPx = Math.max(4, Math.min(14, Math.floor(84 / Math.max(rows, cols))))
        const gap = Math.max(1, Math.floor(cellPx / 4))
        const w = cols * cellPx + (cols - 1) * gap
        const h = rows * cellPx + (rows - 1) * gap
        const schemaHue = (s: string) => {
          let hash = 0
          for (const ch of s) hash = (hash * 31 + ch.charCodeAt(0)) & 0xffff
          return hash % 360
        }
        const overrideCount = Object.keys(cell_schemas).length
        return (
          <div className="mt-1.5 flex items-center gap-3 flex-wrap">
            <svg width={w} height={h} className="text-foreground flex-shrink-0">
              {Array.from({ length: rows }, (_, ri) =>
                Array.from({ length: cols }, (_, ci) => {
                  const key = `${ri},${ci}`
                  const cellSchema = cell_schemas[key]
                  const isChanged = ri === r && ci === c
                  return (
                    <rect
                      key={key}
                      x={ci * (cellPx + gap)}
                      y={ri * (cellPx + gap)}
                      width={cellPx}
                      height={cellPx}
                      fill={cellSchema ? `hsl(${schemaHue(cellSchema)},55%,65%)` : "currentColor"}
                      fillOpacity={cellSchema ? 1 : 0.2}
                      stroke={isChanged ? "currentColor" : "none"}
                      strokeWidth={isChanged ? 1.5 : 0}
                      strokeOpacity={0.9}
                      rx={Math.max(0.5, cellPx / 5)}
                    />
                  )
                })
              )}
            </svg>
            <div className="flex flex-col gap-0.5 text-sm">
              <span className="text-foreground font-medium">
                row {r}, col {c}{style ? ` → ${style.replace(/_/g, " ")}` : " cleared"}
              </span>
              <span className="opacity-60">
                {overrideCount} override{overrideCount !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        )
      })()}
    </div>
  )
}, toolVisualPropsEqual)


// Yardage gauge: tick-marked bar with whole-yard ticks, fill scaled to yards.
function YardageBar({ yards }: { yards: number }) {
  const max = Math.max(4, Math.ceil(yards))
  const w = 96
  const h = 8
  const fillW = Math.max(2, Math.min(w, (yards / max) * w))
  const ticks: React.ReactElement[] = []
  for (let i = 1; i < max; i++) {
    const x = (i / max) * w
    ticks.push(<line key={i} x1={x} x2={x} y1={0} y2={h} stroke="currentColor" strokeOpacity={0.18} strokeWidth={1} />)
  }
  return (
    <svg width={w} height={h} className="text-foreground flex-shrink-0" aria-hidden>
      <rect x={0} y={0} width={w} height={h} rx={2} fill="currentColor" opacity={0.08} />
      <rect x={0} y={0} width={fillW} height={h} rx={2} fill="currentColor" opacity={0.55} />
      {ticks}
    </svg>
  )
}

// Schema-driven mini block preview. Renders whatever block the active style
// describes (Sawtooth Star, Nine-Patch, …) by delegating to the same
// `renderBlock` SVG used everywhere else.
function BlockRolesDiagram({ block }: { block: Block }) {
  const colors = { ...DEFAULT_BLOCK_COLORS, stroke: "#0002" }
  const target = 42
  const [cols, rows] = getSchema(block.style).grid_units
  const longest = Math.max(cols, rows) * Math.max(0.5, block.unit_inches)
  const scale = target / longest
  const svgW = Math.round(cols * (target / Math.max(cols, rows)))
  const svgH = Math.round(rows * (target / Math.max(cols, rows)))
  const svg = renderBlock(block, scale, colors)
  return (
    <span
      className="rounded border border-black/10 flex-shrink-0 inline-block overflow-hidden [&>svg]:block"
      style={{ width: svgW, height: svgH }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}

// Small dot grid representing the rows × cols layout.
function LayoutDotGrid({ rows, cols }: { rows: number; cols: number }) {
  if (rows <= 0 || cols <= 0) return null
  const maxSide = 64
  const cell = Math.max(2, Math.min(8, Math.floor(maxSide / Math.max(rows, cols))))
  const gap = Math.max(1, Math.floor(cell / 3))
  const w = cols * cell + (cols - 1) * gap
  const h = rows * cell + (rows - 1) * gap
  const dots: React.ReactElement[] = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      dots.push(
        <rect
          key={`${r}-${c}`}
          x={c * (cell + gap)}
          y={r * (cell + gap)}
          width={cell}
          height={cell}
          rx={Math.max(0.5, cell / 4)}
          fill="currentColor"
          opacity={0.55}
        />
      )
    }
  }
  return (
    <svg width={w} height={h} className="text-foreground flex-shrink-0">
      {dots}
    </svg>
  )
}

const MENU_ICON_MAP: Record<MenuIconName, React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>> = {
  add:      Plus,
  edit:     Pencil,
  remove:   Trash2,
  block:    Square,
  quilt:    Grid3x3,
  layout:   LayoutGrid,
  cut:      Scissors,
  plan:     FileText,
  colors:   Palette,
  name:     Tag,
  measure:  Ruler,
  fabric:   Package,
  info:     Info,
  surprise: Wand2,
  random:   Dices,
  print:    Printer,
  play:     Play,
  next:     ArrowRight,
}

function MenuIcon({ name }: { name: MenuIconName }) {
  const Icon = MENU_ICON_MAP[name]
  return <Icon size={16} strokeWidth={1.75} className="text-muted-foreground" />
}

function MicIcon({ active, denied }: { active: boolean; denied?: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0M12 19v4M8 23h8" strokeLinecap="round" strokeDasharray={active ? undefined : "2 2"} />
      {denied && <line x1="4" y1="4" x2="20" y2="20" strokeLinecap="round" />}
    </svg>
  )
}

function SendIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 2L11 13M22 2L15 22 11 13 2 9l20-7z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
